import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { projectsApi } from '@/services';

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

interface ProjectState {
  projects: any[];
  project: any | null;
  pagination: Pagination;
  loading: boolean;
  error: any | null;
}

const initialState: ProjectState = {
  projects: [],
  project: null,
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  },
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await projectsApi.getAll(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await projectsApi.getById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await projectsApi.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, data }: { id: number | string; data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response = await projectsApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await projectsApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearProject(state) {
      state.project = null;
    },
    clearError(state) {
      state.error = null;
    },
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        // Validate the unwrap: `action.payload.data || []` silently accepted a
        // non-array payload, leaving `state.projects` an object that breaks
        // every .map/.length downstream.
        const payload = action.payload as { data?: unknown; meta?: Record<string, unknown> };
        const list = Array.isArray(payload?.data) ? payload.data : [];
        state.projects = list;
        const meta = (payload?.meta ?? {}) as Partial<Pagination> & { totalPages?: number };
        state.pagination = {
          // `??` not `||`: a server that legitimately reports total 0 / perPage 0
          // was being overridden by the client-side fallbacks, and
          // `meta.total || list.length` reported the PAGE size as the total on
          // page 2+.
          currentPage: meta.currentPage ?? 1,
          lastPage: meta.totalPages ?? meta.lastPage ?? 1,
          perPage: meta.perPage ?? 10,
          total: meta.total ?? list.length,
        };
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        state.project = action.payload.data || action.payload;
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        const newProject = action.payload.data || action.payload;
        state.projects.unshift(newProject);
        // The row was prepended, so the header total must grow with it.
        state.pagination.total += 1;
        state.pagination.lastPage = Math.max(
          state.pagination.lastPage,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProject = action.payload.data || action.payload;
        const index = state.projects.findIndex((p: any) => p.id === updatedProject.id);
        if (index !== -1) {
          state.projects[index] = updatedProject;
        }
        if (state.project?.id === updatedProject.id) {
          state.project = updatedProject;
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter((p: any) => p.id !== action.payload);
        if (state.project?.id === action.payload) {
          state.project = null;
        }
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.pagination.lastPage = Math.max(
          1,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearProject, clearError, reset } = projectSlice.actions;
export default projectSlice.reducer;
